import { useEffect } from 'react';
import { api } from './api';

type SiteConfig = Awaited<ReturnType<typeof api.siteConfig>>;

let injected = false;

function injectScript(src: string, attrs: Record<string, string> = {}) {
  if (document.querySelector(`script[src="${src}"]`)) return;
  const script = document.createElement('script');
  script.src = src;
  script.async = true;
  Object.entries(attrs).forEach(([k, v]) => script.setAttribute(k, v));
  document.head.appendChild(script);
}

function injectInline(id: string, contents: string) {
  if (document.getElementById(id)) return;
  const script = document.createElement('script');
  script.id = id;
  script.textContent = contents;
  document.head.appendChild(script);
}

function injectMeta(name: string, content: string) {
  if (!content) return;
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function applyConfig(config: SiteConfig) {
  if (injected) return;
  injected = true;

  if (config.googleSiteVerification) {
    injectMeta('google-site-verification', config.googleSiteVerification);
  }

  if (config.ga4Id) {
    injectScript(`https://www.googletagmanager.com/gtag/js?id=${config.ga4Id}`);
    injectInline('ga4-init', `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      window.gtag = gtag;
      gtag('js', new Date());
      gtag('config', '${config.ga4Id}', { send_page_view: true });
    `);
  }

  if (config.gtmId) {
    injectInline('gtm-init', `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});
      var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;
      j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${config.gtmId}');
    `);
  }

  if (config.metaPixelId) {
    injectInline('meta-pixel-init', `
      !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
      n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '${config.metaPixelId}');
      fbq('track', 'PageView');
    `);
  }

  if (config.tiktokPixelId) {
    injectInline('tiktok-pixel-init', `
      !function (w, d, t) {
        w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=r,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};n=document.createElement("script");n.type="text/javascript",n.async=!0,n.src=r+"?sdkid="+e+"&lib="+t;e=document.getElementsByTagName("script")[0];e.parentNode.insertBefore(n,e)};
        ttq.load('${config.tiktokPixelId}');
        ttq.page();
      }(window, document, 'ttq');
    `);
  }

  if (config.hotjarId) {
    injectInline('hotjar-init', `
      (function(h,o,t,j,a,r){
        h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
        h._hjSettings={hjid:${config.hotjarId},hjsv:6};
        a=o.getElementsByTagName('head')[0];
        r=o.createElement('script');r.async=1;
        r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
        a.appendChild(r);
      })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
    `);
  }
}

export function trackPageView(path: string) {
  // GA4
  if (typeof window !== 'undefined' && (window as { gtag?: (...args: unknown[]) => void }).gtag) {
    (window as { gtag?: (...args: unknown[]) => void }).gtag!('event', 'page_view', { page_path: path });
  }
  // Meta Pixel
  if (typeof window !== 'undefined' && (window as { fbq?: (...args: unknown[]) => void }).fbq) {
    (window as { fbq?: (...args: unknown[]) => void }).fbq!('track', 'PageView');
  }
  // TikTok
  if (typeof window !== 'undefined' && (window as { ttq?: { page: () => void } }).ttq) {
    (window as { ttq?: { page: () => void } }).ttq!.page();
  }
}

export function useSiteTracking() {
  useEffect(() => {
    let cancelled = false;
    api.siteConfig().then((config) => {
      if (!cancelled) applyConfig(config);
    }).catch(() => {
      // No-op si falla, es opcional
    });
    return () => { cancelled = true; };
  }, []);
}
